import React, { ReactNode, CSSProperties } from "react";
import ButtonBase, { ButtonBaseProps } from "@material-ui/core/ButtonBase";
import { makeStyles, createStyles, Theme, fade } from "@material-ui/core/styles";
import { transition, fonts } from "theme";
import clsx from "clsx";
import assetLocations from "assetLocations";

export type StyledButtonVariant = "primary" | "secondary" | "white" | "transparent";
export type StyledButtonSize = "small" | "medium" | "large" | "extralarge";

const useStyles = (uppercase: boolean, backgroundImageSrc?: string) =>
    makeStyles((theme: Theme) => {
        const getButtonPadding = (height: number, width: number) => `${height}px ${width}px`;

        return createStyles({
            root: {
                transition: `all ${transition.duration} ease-in-out`,
                backgroundImage:
                    typeof backgroundImageSrc === "undefined"
                        ? "unset"
                        : `url(${backgroundImageSrc})`,
                backgroundSize: "100% 100%",
            },
            primary: {
                color: theme.palette.common.black,
                "& $text": {
                    border: `1px solid ${fade(theme.palette.common.white, 96)}`,
                    textShadow: "0px 0px 8px orangered",
                },
                "&:hover $text": {
                    backgroundColor: theme.palette.common.black,
                    color: theme.palette.common.white,
                    opacity: 0.9,
                },
            },
            secondary: {
                color: theme.palette.common.white,
                "&:hover $text": {
                    color: theme.palette.secondary.main,
                },
            },
            white: {
                color: theme.palette.common.black,
                "& $text": {
                    border: `1px solid ${fade(theme.palette.common.black, 96)}`,
                },
                "&:hover $text": {
                    backgroundColor: theme.palette.common.black,
                    color: theme.palette.common.white,
                    textShadow: "0px 0px 8px orangered",
                    opacity: 0.9,
                },
            },
            transparent: {
                color: theme.palette.secondary.main,
                border: `1px solid ${theme.palette.secondary.main}`,
                "&:hover": {
                    color: theme.palette.common.black,
                    backgroundColor: theme.palette.secondary.main,
                },
            },
            small: {
                padding: getButtonPadding(5, 4),
                fontSize: "100%", // bug in firefox with weird positioning on hover if fontweight is a whole number
                "& $text": {
                    padding: getButtonPadding(1, 8),
                },
            },
            medium: {
                padding: getButtonPadding(6, 6),
                fontSize: "135%",
                "& $text": {
                    padding: getButtonPadding(3, 10),
                },
            },
            large: {
                padding: getButtonPadding(6, 8),
                fontSize: "170%",
                "& $text": {
                    padding: getButtonPadding(4, 12),
                },
            },
            extralarge: {
                padding: getButtonPadding(10, 12),
                fontSize: "250%",
                "& $text": {
                    padding: getButtonPadding(4, 12),
                },
            },
            text: {
                width: "100%",
                lineHeight: 1,
                textAlign: "center",
                textTransform: uppercase ? "uppercase" : "none",
                transition: `all ${transition.duration} ease-in-out`,
                fontFamily: fonts.secondary,
                fontWeight: 900,
            },
            disabled: {
                color: theme.palette.common.white,
                "& $text": {
                    textShadow: "none",
                },
            },
        });
    });

export interface IStyledButtonProps extends ButtonBaseProps {
    children?: ReactNode;
    uppercase?: boolean;
    variant?: StyledButtonVariant;
    size?: StyledButtonSize;
    textStyle?: CSSProperties;
}

export default function StyledButton(props: IStyledButtonProps) {
    const {
        children,
        className = "",
        uppercase = true,
        variant = "primary",
        disabled = false,
        size = "medium",
        textStyle = {},
        ...rest
    } = props;

    const backgroundImageSrc = disabled
        ? assetLocations.buttonBackgrounds.disabled
        : variant === "transparent"
        ? undefined
        : assetLocations.buttonBackgrounds[variant];

    const classes = useStyles(uppercase, backgroundImageSrc)();
    return (
        <ButtonBase
            className={clsx(classes.root, className, classes[variant], classes[size], {
                [classes.disabled]: disabled,
            })}
            disabled={disabled}
            {...rest}
        >
            <div className={classes.text} style={textStyle}>
                {children}
            </div>
        </ButtonBase>
    );
}
